import { useEffect, useState, useRef, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ThreeAvatar from "@/components/ThreeAvatar";
import SelfieCapture from "@/components/SelfieCapture";
import {
  APPAREL_BRANDS,
  PALA_BRANDS,
  SHOES_BRANDS,
  AVATAR_BRANDS,
} from "@/lib/avatar";
// Fallback list only used when the backend catalog is empty (offline / auth error).
// The single source of truth is `/api/gear-catalog` — deletions and additions
// there reflect immediately in the picker below.
const _FALLBACK = { apparel: APPAREL_BRANDS, pala: PALA_BRANDS, shoes: SHOES_BRANDS, avatar_brand: AVATAR_BRANDS };
import { FloppyDisk, ArrowClockwise, Sparkle, TShirt, Sneaker, BaseballCap, Palette, Camera, ShareNetwork, ChartBar } from "@phosphor-icons/react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const absLogo = (u) => {
  if (!u) return null;
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/")) return `${API_URL}${u}`;
  return u;
};

// Convert a gear-catalog row into the shape AvatarStudio expects.
function normalizeCatalog(list) {
  return (Array.isArray(list) ? list : []).map((it) => ({
    id: it.id,
    name: it.name,
    color: it.color || it.primary || "#0A0A0A",
    text: it.text || it.accent || "#FFFFFF",
    short: it.short || (it.name || "").slice(0, 3).toUpperCase(),
    logo: it.logo || null,
  }));
}

const SKIN_TONES = [
  { id: "fair", hex: "#F5D6B7", label: "Claro" },
  { id: "light", hex: "#E8C39E", label: "Medio" },
  { id: "tan", hex: "#C68863", label: "Bronceado" },
  { id: "brown", hex: "#8D5524", label: "Oscuro" },
  { id: "deep", hex: "#5A3115", label: "Profundo" },
];

const PANTS_COLORS = [
  { id: "black", hex: "#0A0A0A", label: "Negro" },
  { id: "navy", hex: "#1B1B3A", label: "Marino" },
  { id: "white", hex: "#F5F5F5", label: "Blanco" },
  { id: "grey", hex: "#3A3A3A", label: "Gris" },
  { id: "red", hex: "#B91C1C", label: "Rojo" },
  { id: "lime", hex: "#84CC16", label: "Lima" },
];

const APPAREL_TYPES = [
  { id: "short", label: "Manga corta" },
  { id: "long", label: "Manga larga" },
  { id: "tank", label: "Tirantes" },
];

const SHOES_TYPES = [
  { id: "low", label: "Bajas" },
  { id: "high", label: "Altas" },
];

const HEADWEAR_OPTS = [
  { id: "none", label: "Ninguno" },
  { id: "cap", label: "Gorra" },
  { id: "band", label: "Cintillo" },
];

const ChipRow = ({ items, value, onChange, render, testIdPrefix }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.map((it) => {
      const active = value === it.id;
      return (
        <button
          key={it.id}
          onClick={() => onChange(it.id)}
          className={`px-2.5 py-1.5 rounded text-[10px] uppercase tracking-wide font-bold border transition-all ${active ? "bg-[#CCFF00] text-black border-[#CCFF00] shadow-[0_0_12px_rgba(204,255,0,0.4)]" : "bg-white/5 text-white border-white/15 hover:bg-white/10"}`}
          data-testid={`${testIdPrefix}-${it.id}`}
        >
          {render ? render(it, active) : it.label}
        </button>
      );
    })}
  </div>
);

const ColorChips = ({ items, value, onChange, testIdPrefix }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.map((c) => (
      <button
        key={c.id}
        onClick={() => onChange(c.hex)}
        className={`w-8 h-8 rounded border-2 transition-all ${value === c.hex ? "border-[#CCFF00] scale-110 shadow-[0_0_8px_rgba(204,255,0,0.6)]" : "border-white/20 hover:border-white/40"}`}
        style={{ backgroundColor: c.hex }}
        title={c.label}
        data-testid={`${testIdPrefix}-${c.id}`}
      />
    ))}
  </div>
);

export default function AvatarStudio() {
  const { user, checkAuth } = useAuth();
  const initial = user?.avatar3d || {};
  const [config, setConfig] = useState({
    apparel: initial.apparel || "short",
    shoesType: initial.shoesType || "low",
    headwear: initial.headwear || "none",
    skin: initial.skin || "#E8C39E",
    pants: initial.pants || "#0A0A0A",
    jersey: initial.jersey || "#CCFF00",
    shoes: initial.shoes || "#E63946",
    pala: initial.pala || "#00A0FF",
    apparelBrand: initial.apparelBrand || (user?.gear_apparel_brand) || "dyd",
    palaBrand: initial.palaBrand || (user?.gear_pala_brand) || "dyd",
    shoesBrand: initial.shoesBrand || (user?.gear_shoes_brand) || "dyd",
    clubBrand: initial.clubBrand || (user?.avatar_brand) || "dyd",
    facePhoto: initial.facePhoto || "",
  });
  const [saving, setSaving] = useState(false);
  const [animation, setAnimation] = useState("idle"); // idle | swing
  const [cameraMode, setCameraMode] = useState("default"); // default | cinema
  const [selfieOpen, setSelfieOpen] = useState(false);
  const autoSaveTimer = useRef(null);
  const isFirstRender = useRef(true);
  // Admin-editable dynamic gear catalog (pala/apparel/shoes/avatar_brand)
  const [dynCatalog, setDynCatalog] = useState({ pala: null, apparel: null, shoes: null, avatar_brand: null });

  useEffect(() => {
    api.get("/gear-catalog").then(({ data }) => {
      setDynCatalog({
        pala: data?.pala || [],
        apparel: data?.apparel || [],
        shoes: data?.shoes || [],
        avatar_brand: data?.avatar_brand || [],
      });
    }).catch(() => {
      setDynCatalog({ pala: [], apparel: [], shoes: [], avatar_brand: [] });
    });
  }, []);

  // Live-refresh the catalog when it changes elsewhere (Admin Gear Catalog page).
  // Also refresh when the browser tab becomes visible again.
  useEffect(() => {
    const reload = () => api.get("/gear-catalog").then(({ data }) => {
      setDynCatalog({
        pala: data?.pala || [],
        apparel: data?.apparel || [],
        shoes: data?.shoes || [],
        avatar_brand: data?.avatar_brand || [],
      });
    }).catch(() => {});
    const onVis = () => { if (document.visibilityState === "visible") reload(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("gear-catalog:changed", reload);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("gear-catalog:changed", reload);
    };
  }, []);

  // Source of truth = backend catalog (respects tombstones/additions). Only
  // fallback to hardcoded lists if the backend response is unreachable (null).
  const _resolve = (key) => {
    const arr = dynCatalog[key];
    return normalizeCatalog(arr === null ? _FALLBACK[key] : arr);
  };
  const apparelBrands = useMemo(() => _resolve("apparel"), [dynCatalog.apparel]);
  const palaBrands    = useMemo(() => _resolve("pala"),    [dynCatalog.pala]);
  const shoesBrands   = useMemo(() => _resolve("shoes"),   [dynCatalog.shoes]);
  const clubBrands    = useMemo(() => _resolve("avatar_brand"), [dynCatalog.avatar_brand]);

  // Apply-brand-color helper now accepts the resolved brand list so
  // dynamic (admin-added) brands work exactly like the defaults.
  const applyBrandColor = (key, brandId, brandsList) => {
    const b = brandsList.find((x) => x.id === brandId);
    if (b) setConfig((c) => ({ ...c, [`${key}Brand`]: brandId, [key]: b.color }));
    else setConfig((c) => ({ ...c, [`${key}Brand`]: brandId }));
  };

  const setCfg = (k, v) => setConfig((c) => ({ ...c, [k]: v }));

  // Debounced auto-save whenever brand selections change so the avatar 3D AR
  // reflects the new logos immediately across the app + public profile views.
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      try {
        await api.put("/users/profile/update", {
          avatar3d: config,
          gear_pala_brand: config.palaBrand,
          gear_apparel_brand: config.apparelBrand,
          gear_shoes_brand: config.shoesBrand,
          avatar_brand: config.clubBrand,
        });
        toast.success("Marca guardada · Avatar 3D AR actualizado", { duration: 1600 });
        checkAuth?.();
      } catch {
        /* silent — user can hit "Guardar" manually */
      }
    }, 600);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.apparelBrand, config.palaBrand, config.shoesBrand, config.clubBrand]);

  const reset = () => setConfig({
    apparel: "short",
    shoesType: "low",
    headwear: "none",
    skin: "#E8C39E",
    pants: "#0A0A0A",
    jersey: "#CCFF00",
    shoes: "#E63946",
    pala: "#00A0FF",
    apparelBrand: "dyd",
    palaBrand: "dyd",
    shoesBrand: "dyd",
    clubBrand: "dyd",
    facePhoto: "",
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/users/profile/update", {
        avatar3d: config,
        gear_pala_brand: config.palaBrand,
        gear_apparel_brand: config.apparelBrand,
        gear_shoes_brand: config.shoesBrand,
        avatar_brand: config.clubBrand,
      });
      toast.success("Avatar 3D guardado");
      checkAuth?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const sharePublicProfile = async () => {
    if (!user?.id) { toast.error("Inicia sesión para compartir"); return; }
    const url = `${window.location.origin}/p/${user.id}`;
    const text = `${user.name || "Mi perfil"} · Disfruta y Diviértete`;
    try {
      if (navigator.share) {
        await navigator.share({ title: text, text, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Enlace público copiado");
      }
    } catch (e) {
      // user dismissed share sheet — non-fatal
    }
  };

  const clubBrand = clubBrands.find((b) => b.id === config.clubBrand) || null;
  const palaBrand = palaBrands.find((b) => b.id === config.palaBrand) || null;
  const apparelBrand = apparelBrands.find((b) => b.id === config.apparelBrand) || null;
  const shoesBrand = shoesBrands.find((b) => b.id === config.shoesBrand) || null;
  const clubLogoUrl = absLogo(clubBrand?.logo);
  const palaLogoUrl = absLogo(palaBrand?.logo);
  // Chest → apparel brand, fallback to sponsor/club
  const chestLogoUrl = absLogo(apparelBrand?.logo) || clubLogoUrl || null;
  // Short → apparel brand (same as chest since same "kit")
  const shortLogoUrl = absLogo(apparelBrand?.logo);
  // Shoes → shoes brand
  const shoesLogoUrl = absLogo(shoesBrand?.logo);
  // Cap → sponsor / club brand
  const capLogoUrl = clubLogoUrl;

  return (
    <div className="card-tactical p-4" data-testid="avatar-studio">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#CCFF00] flex items-center gap-1">
            <Sparkle size={12} weight="fill" /> AR 3D · Avatar Studio
          </p>
          <h3 className="font-['Barlow_Condensed'] text-2xl font-black uppercase tracking-tight">Personaliza tu jugador</h3>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={sharePublicProfile} size="sm" variant="outline" className="border-[#CCFF00]/40 text-[#CCFF00] hover:bg-[#CCFF00]/10 text-[10px] uppercase font-bold h-8" data-testid="avatar-studio-share">
            <ShareNetwork size={12} className="mr-1" /> Compartir perfil
          </Button>
          <Button onClick={reset} size="sm" variant="outline" className="border-white/15 text-[10px] uppercase font-bold h-8" data-testid="avatar-studio-reset">
            <ArrowClockwise size={12} className="mr-1" /> Reset
          </Button>
          <Button onClick={save} disabled={saving} size="sm" className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold text-[10px] uppercase h-8" data-testid="avatar-studio-save">
            <FloppyDisk size={12} className="mr-1" /> {saving ? "Guardando..." : "Guardar avatar"}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-4">
        {/* 3D Avatar with cinematic + animation controls */}
        <div className="relative">
          <ThreeAvatar
            brandColors={{ jersey: config.jersey, pants: config.pants, shoes: config.shoes, pala: config.pala, skin: config.skin }}
            outfit={{ apparel: config.apparel, shoes: config.shoesType, headwear: config.headwear }}
            clubLogoUrl={clubLogoUrl}
            palaLogoUrl={palaLogoUrl}
            chestLogoUrl={chestLogoUrl}
            shortLogoUrl={shortLogoUrl}
            shoesLogoUrl={shoesLogoUrl}
            capLogoUrl={capLogoUrl}
            animation={animation}
            cameraMode={cameraMode}
            facePhotoUrl={config.facePhoto || null}
            height={480}
          />
          {/* Floating Camera FAB — opens selfie modal */}
          <button
            onClick={() => setSelfieOpen(true)}
            className="absolute top-2 right-2 w-11 h-11 rounded-full bg-[#CCFF00] text-black shadow-[0_0_24px_rgba(204,255,0,0.5)] hover:scale-110 transition-transform flex items-center justify-center font-bold border-2 border-black"
            title="Personalizar rostro"
            data-testid="avatar-selfie-fab"
          >
            <Camera size={20} weight="fill" />
          </button>
          {config.facePhoto && (
            <div className="absolute bottom-12 right-2 px-2 py-0.5 text-[9px] uppercase tracking-wide font-bold bg-black/60 backdrop-blur-sm text-[#CCFF00] rounded border border-[#CCFF00]/30 pointer-events-none">
              Rostro custom
            </div>
          )}
          {/* Overlay: animation + camera controls */}
          <div className="absolute top-2 left-2 flex flex-col gap-1.5" data-testid="avatar-overlay-controls">
            <div className="flex gap-1 bg-black/70 backdrop-blur-sm rounded p-1 border border-white/10">
              <button
                onClick={() => setAnimation("idle")}
                className={`px-2 py-1 text-[9px] uppercase tracking-wide font-bold rounded transition-all ${animation === "idle" ? "bg-[#CCFF00] text-black" : "text-gray-400 hover:text-white"}`}
                data-testid="anim-idle"
              >
                Idle
              </button>
              <button
                onClick={() => setAnimation("swing")}
                className={`px-2 py-1 text-[9px] uppercase tracking-wide font-bold rounded transition-all ${animation === "swing" ? "bg-[#CCFF00] text-black" : "text-gray-400 hover:text-white"}`}
                data-testid="anim-swing"
              >
                Swing
              </button>
            </div>
            <div className="flex gap-1 bg-black/70 backdrop-blur-sm rounded p-1 border border-white/10">
              <button
                onClick={() => setCameraMode("default")}
                className={`px-2 py-1 text-[9px] uppercase tracking-wide font-bold rounded transition-all ${cameraMode === "default" ? "bg-[#00A0FF] text-black" : "text-gray-400 hover:text-white"}`}
                data-testid="cam-default"
              >
                Frontal
              </button>
              <button
                onClick={() => setCameraMode("cinema")}
                className={`px-2 py-1 text-[9px] uppercase tracking-wide font-bold rounded transition-all ${cameraMode === "cinema" ? "bg-[#00A0FF] text-black" : "text-gray-400 hover:text-white"}`}
                data-testid="cam-cinema"
              >
                Vista 360
              </button>
            </div>
          </div>
        </div>

        {/* Controls panel */}
        <div className="space-y-4 max-h-[480px] overflow-y-auto pr-1 custom-scroll">
          {/* Skin */}
          <Section title="Tono de piel" icon={<Palette size={11} />}>
            <ColorChips items={SKIN_TONES} value={config.skin} onChange={(v) => setCfg("skin", v)} testIdPrefix="skin" />
          </Section>

          {/* Apparel type */}
          <Section title="Tipo de camiseta" icon={<TShirt size={11} />}>
            <ChipRow items={APPAREL_TYPES} value={config.apparel} onChange={(v) => setCfg("apparel", v)} testIdPrefix="apparel-type" />
          </Section>

          {/* Apparel color & brand */}
          <Section title="Color camiseta">
            <ColorChips items={[
              { id: "lime", hex: "#CCFF00", label: "Lima" },
              { id: "blue", hex: "#0066FF", label: "Azul" },
              { id: "red", hex: "#E63946", label: "Rojo" },
              { id: "purple", hex: "#7C4DFF", label: "Morado" },
              { id: "orange", hex: "#FF6B00", label: "Naranja" },
              { id: "white", hex: "#FFFFFF", label: "Blanco" },
              { id: "black", hex: "#0A0A0A", label: "Negro" },
            ]} value={config.jersey} onChange={(v) => setCfg("jersey", v)} testIdPrefix="jersey-color" />
          </Section>

          <Section title="Marca camiseta">
            <BrandGrid items={apparelBrands} value={config.apparelBrand} onChange={(id) => applyBrandColor("jersey", id, apparelBrands)} testIdPrefix="apparel-brand" small />
          </Section>

          {/* Pants */}
          <Section title="Color pantalón">
            <ColorChips items={PANTS_COLORS} value={config.pants} onChange={(v) => setCfg("pants", v)} testIdPrefix="pants-color" />
          </Section>

          {/* Shoes */}
          <Section title="Tipo de calzado" icon={<Sneaker size={11} />}>
            <ChipRow items={SHOES_TYPES} value={config.shoesType} onChange={(v) => setCfg("shoesType", v)} testIdPrefix="shoes-type" />
          </Section>

          <Section title="Color calzado">
            <ColorChips items={[
              { id: "white", hex: "#F5F5F5", label: "Blanco" },
              { id: "red", hex: "#E63946", label: "Rojo" },
              { id: "black", hex: "#0A0A0A", label: "Negro" },
              { id: "yellow", hex: "#FFEB00", label: "Amarillo" },
              { id: "blue", hex: "#0066FF", label: "Azul" },
              { id: "lime", hex: "#CCFF00", label: "Lima" },
            ]} value={config.shoes} onChange={(v) => setCfg("shoes", v)} testIdPrefix="shoes-color" />
          </Section>

          <Section title="Marca calzado">
            <BrandGrid items={shoesBrands} value={config.shoesBrand} onChange={(id) => applyBrandColor("shoes", id, shoesBrands)} testIdPrefix="shoes-brand" small />
          </Section>

          {/* Headwear */}
          <Section title="Accesorio cabeza" icon={<BaseballCap size={11} />}>
            <ChipRow items={HEADWEAR_OPTS} value={config.headwear} onChange={(v) => setCfg("headwear", v)} testIdPrefix="headwear" />
          </Section>

          {/* Pala */}
          <Section title="Color pala">
            <ColorChips items={[
              { id: "blue", hex: "#00A0FF", label: "Azul" },
              { id: "lime", hex: "#CCFF00", label: "Lima" },
              { id: "yellow", hex: "#FFEB00", label: "Amarillo" },
              { id: "red", hex: "#E63946", label: "Rojo" },
              { id: "black", hex: "#0A0A0A", label: "Negro" },
              { id: "orange", hex: "#FF6B00", label: "Naranja" },
            ]} value={config.pala} onChange={(v) => setCfg("pala", v)} testIdPrefix="pala-color" />
          </Section>

          <Section title="Marca pala">
            <BrandGrid items={palaBrands} value={config.palaBrand} onChange={(id) => applyBrandColor("pala", id, palaBrands)} testIdPrefix="pala-brand" small />
          </Section>

          {/* Club */}
          <Section title="Club / Sponsor en pecho">
            <BrandGrid items={clubBrands} value={config.clubBrand} onChange={(id) => setCfg("clubBrand", id)} testIdPrefix="club-brand" />
          </Section>
        </div>
      </div>

      {/* Admin-only brand ranking (all brands used across every player) */}
      {user?.role === "admin" && (
        <BrandRankingAdmin
          apparelBrands={apparelBrands}
          palaBrands={palaBrands}
          shoesBrands={shoesBrands}
          clubBrands={clubBrands}
        />
      )}

      {/* Selfie modal */}
      <SelfieCapture
        open={selfieOpen}
        onClose={() => setSelfieOpen(false)}
        onSelected={(b64) => setCfg("facePhoto", b64)}
        currentPhoto={config.facePhoto}
      />
    </div>
  );
}

const Section = ({ title, icon, children }) => (
  <div className="space-y-1.5">
    <div className="text-[9px] tracking-[0.2em] uppercase text-gray-400 font-bold flex items-center gap-1">
      {icon}{title}
    </div>
    {children}
  </div>
);

const BrandGrid = ({ items, value, onChange, testIdPrefix, small = false }) => (
  <div className="grid grid-cols-3 gap-1.5">
    {items.map((b) => {
      const active = value === b.id;
      const logoUrl = absLogo(b.logo);
      const size = small ? "w-6 h-6" : "w-7 h-7";
      return (
        <button
          key={b.id}
          onClick={() => onChange(b.id)}
          className={`flex items-center gap-1.5 px-1.5 py-1 rounded border text-[9px] uppercase font-bold transition-all ${active ? "bg-[#CCFF00]/15 border-[#CCFF00] text-[#CCFF00] shadow-[0_0_8px_rgba(204,255,0,0.3)]" : "bg-white/5 border-white/10 text-white hover:bg-white/10"}`}
          data-testid={`${testIdPrefix}-${b.id}`}
          title={b.name}
        >
          {logoUrl ? (
            <span className={`shrink-0 rounded-sm overflow-hidden bg-white flex items-center justify-center border border-white/10 ${size}`}>
              <img src={logoUrl} alt={b.name} className="w-full h-full object-contain" loading="lazy" />
            </span>
          ) : (
            <span
              className={`shrink-0 rounded-sm flex items-center justify-center font-black ${small ? "w-5 h-5 text-[7px]" : "w-6 h-6 text-[8px]"}`}
              style={{ backgroundColor: b.color || b.primary, color: b.text || b.accent || "#fff" }}
            >
              {b.short || (b.name || "").slice(0, 3).toUpperCase()}
            </span>
          )}
          <span className="truncate flex-1 text-left">{b.name}</span>
        </button>
      );
    })}
  </div>
);


// ============================================================
// Admin-only Brand Ranking — shows all brands used by players
// ============================================================
function BrandRankingAdmin({ apparelBrands, palaBrands, shoesBrands, clubBrands }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("apparel");
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/admin/brand-ranking")
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e.response?.data?.detail || "Error"))
      .finally(() => setLoading(false));
  }, []);

  // Resolve display metadata (name / logo / color) using the merged brand list
  // so hardcoded ids (e.g. "dyd") also show a readable name in the ranking.
  const resolve = (list, id, fallbackRow) => {
    const b = list.find((x) => x.id === id);
    return {
      name: b?.name || fallbackRow?.name || id,
      logo: fallbackRow?.logo || b?.logo || null,
      color: fallbackRow?.color || b?.color || b?.primary || "#0A0A0A",
      text: b?.text || b?.accent || "#FFFFFF",
    };
  };

  const TABS = [
    { id: "apparel",      label: "Camisetas",  list: apparelBrands },
    { id: "pala",         label: "Palas",      list: palaBrands },
    { id: "shoes",        label: "Calzado",    list: shoesBrands },
    { id: "avatar_brand", label: "Club / Sponsor", list: clubBrands },
  ];
  const active = TABS.find((t) => t.id === tab);
  const rows = data?.[tab] || [];
  const totalCount = rows.reduce((s, r) => s + (r.count || 0), 0);

  return (
    <div className="mt-4 border-t border-white/10 pt-4" data-testid="admin-brand-ranking">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="px-1.5 py-0.5 rounded text-[8px] uppercase font-black bg-red-500/20 text-red-400 border border-red-500/40 tracking-widest">Admin</span>
          <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#CCFF00] flex items-center gap-1">
            <ChartBar size={12} weight="fill" /> Ranking de marcas
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {data && (
            <span className="text-[10px] uppercase tracking-widest text-gray-500" data-testid="admin-brand-ranking-total">
              {data.total_users} usuarios · {rows.length} marcas activas en {active?.label}
            </span>
          )}
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/brand-ranking.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-2 py-1 rounded text-[10px] uppercase font-bold bg-[#CCFF00] text-black hover:bg-[#B3E600] flex items-center gap-1"
            data-testid="admin-brand-ranking-pdf"
          >
            📄 PDF
          </a>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-2.5 py-1 rounded text-[10px] uppercase font-bold border transition-all ${tab === t.id ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-white/5 text-gray-300 border-white/10 hover:bg-white/10"}`}
            data-testid={`admin-brand-tab-${t.id}`}
          >
            {t.label} <span className="opacity-60">({(data?.[t.id] || []).length})</span>
          </button>
        ))}
      </div>

      {loading && (
        <p className="text-xs text-gray-500 py-4 text-center" data-testid="admin-brand-ranking-loading">Cargando…</p>
      )}
      {err && !loading && (
        <p className="text-xs text-red-400 py-4 text-center" data-testid="admin-brand-ranking-error">{err}</p>
      )}
      {!loading && !err && rows.length === 0 && (
        <p className="text-xs text-gray-500 py-4 text-center" data-testid="admin-brand-ranking-empty">
          Aún no hay usuarios con marca de {active?.label.toLowerCase()} seleccionada.
        </p>
      )}
      {!loading && !err && rows.length > 0 && (
        <div className="space-y-1.5" data-testid={`admin-brand-ranking-list-${tab}`}>
          {rows.map((r, idx) => {
            const meta = resolve(active.list, r.id, r);
            const logoUrl = absLogo(meta.logo);
            const pct = totalCount > 0 ? Math.round((r.count / totalCount) * 100) : 0;
            return (
              <div
                key={r.id}
                className="flex items-center gap-2 bg-black/30 border border-white/10 rounded p-2"
                data-testid={`admin-brand-row-${r.id}`}
              >
                <span className="text-[10px] tracking-widest text-gray-500 w-6 text-right font-['JetBrains_Mono']">
                  #{idx + 1}
                </span>
                {logoUrl ? (
                  <span className="w-8 h-8 rounded bg-white flex items-center justify-center border border-white/10 shrink-0 overflow-hidden">
                    <img src={logoUrl} alt={meta.name} className="w-full h-full object-contain" loading="lazy" />
                  </span>
                ) : (
                  <span
                    className="w-8 h-8 rounded flex items-center justify-center border border-white/10 shrink-0 font-black text-[9px]"
                    style={{ backgroundColor: meta.color, color: meta.text }}
                  >
                    {(meta.name || "?").slice(0, 3).toUpperCase()}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-white truncate">{meta.name}</p>
                  <div className="h-1 mt-1 bg-white/5 overflow-hidden rounded-full">
                    <div className="h-full bg-[#CCFF00]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <span className="font-['Barlow_Condensed'] font-black text-2xl text-[#CCFF00] leading-none">{r.count}</span>
                  <p className="text-[9px] uppercase tracking-widest text-gray-500 mt-0.5">{pct}%</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
